@file:Suppress("MagicNumber", "TooGenericExceptionCaught")

package com.nonopn.skydome.common.app.pages.projects

import com.nonopn.dispatchers.DefaultDispatcher
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import com.nonopn.skydome.common.app.pages.main.model.api.ApiEstimatesData
import com.nonopn.skydome.common.app.pages.main.model.api.Probe
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.main.model.api.SectionProjectAndThunderDetection
import com.nonopn.skywatcher.app.utils.safeLaunch
import eu.codlab.viewmodel.StateViewModel
import korlibs.time.DateTime
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.pow
import kotlin.math.sin
import kotlin.math.sqrt

// ── Model types ─────────────────────────────────────────────────────────────

/** Avg / max / count by polarity (kA) for activities within a given radius ring. Computed client-side from topActivity. */
data class ZoneStats(
    val countNeg: Int = 0,
    val countPos: Int = 0,
    val avgNegKa: Double = 0.0,
    val maxNegKa: Double = 0.0,
    val maxNegDate: String = "",
    val avgPosKa: Double = 0.0,
    val maxPosKa: Double = 0.0,
    val maxPosDate: String = "",
    val totalCount: Int = 0,
)

data class ProjectWithStats(
    val project: Project,
    val nearestProbe: Probe?,
    val nearestProbeDistanceKm: Double,
    val activities1Km: Int,
    val activities2Km: Int,
    val top: List<SectionProjectAndThunderDetection.Top>,
    val lastAlertSeconds: Long,
    val stats1Km: ZoneStats = ZoneStats(),
    val stats2Km: ZoneStats = ZoneStats(),
) {
    val count1Km: Int = activities1Km
    val count2Km: Int = activities2Km

    // Top 5 impacts sorted by server (highest probability first)
    val top5: List<SectionProjectAndThunderDetection.Top> = top.take(5)

    // Risk score heuristic: 0-99 (lower = higher risk)
    val score: Int = when {
        top5.any { it.percent >= 0.8 } -> (40 - count1Km * 5).coerceIn(10, 40)
        top5.any { it.percent >= 0.3 } -> (65 - count1Km * 3).coerceIn(45, 65)
        count1Km > 0 -> 75
        count2Km > 0 -> 85
        else -> 95
    }
}

enum class ProjectsIncidenceFilter { ALL, HAS_1KM, HAS_2KM, NO_IMPACT }

enum class ProjectsSortOrder { LAST_ALERT, NAME, IMPACTS, CREATED }

data class ProjectsState(
    val isLoading: Boolean = true,
    val items: List<ProjectWithStats> = emptyList(),
    val probes: List<Probe> = emptyList(),
    val selectedProbeUuid: String? = null,
    val incidenceFilter: ProjectsIncidenceFilter = ProjectsIncidenceFilter.ALL,
    val sortOrder: ProjectsSortOrder = ProjectsSortOrder.LAST_ALERT,
    val searchQuery: String = "",
    val error: String? = null,
) {
    // Global summary computed from all items (regardless of filters)
    val totalProjects: Int get() = items.size
    val totalImpacts1Km: Int get() = items.sumOf { it.count1Km }
    val totalImpacts2Km: Int get() = items.sumOf { it.count2Km }
    val lastIncidenceSeconds: Long get() = items.maxOfOrNull { it.lastAlertSeconds } ?: 0L

    val filtered: List<ProjectWithStats>
        get() {
            var list = items
            if (selectedProbeUuid != null) {
                list = list.filter {
                    it.nearestProbe?.uuid == selectedProbeUuid &&
                        it.nearestProbeDistanceKm <= 100.0
                }
            }
            list = when (incidenceFilter) {
                ProjectsIncidenceFilter.HAS_1KM -> list.filter { it.count1Km > 0 }
                ProjectsIncidenceFilter.HAS_2KM -> list.filter { it.count2Km > 0 }
                ProjectsIncidenceFilter.NO_IMPACT -> list.filter { it.count1Km == 0 && it.count2Km == 0 }
                ProjectsIncidenceFilter.ALL -> list
            }
            if (searchQuery.isNotBlank()) {
                list = list.filter { projectMatchesSearch(it.project.name, it.nearestProbe?.name, searchQuery) }
            }
            list = when (sortOrder) {
                ProjectsSortOrder.LAST_ALERT -> list.sortedWith(
                    compareByDescending<ProjectWithStats> { it.lastAlertSeconds }
                        .thenByDescending { it.count1Km + it.count2Km }
                )
                ProjectsSortOrder.NAME -> list.sortedBy { it.project.name.lowercase() }
                ProjectsSortOrder.IMPACTS -> list.sortedByDescending { it.count1Km * 10 + it.count2Km }
                ProjectsSortOrder.CREATED -> list.sortedByDescending { it.project.createdAt ?: 0L }
            }
            return list
        }
}

// ── ViewModel ────────────────────────────────────────────────────────────────

class ProjectsModel(
    private val appModel: AppModel
) : StateViewModel<ProjectsState>(ProjectsState()) {

    private val scope = CoroutineScope(DefaultDispatcher + SupervisorJob())

    init {
        load()
    }

    fun load() = safeLaunch {
        scope.async {
            updateState { copy(isLoading = true, error = null) }
            try {
                val api = appModel.states.value.currentApi ?: run {
                    updateState { copy(isLoading = false) }
                    return@async
                }

                // Probes: use cached v2 dashboard data (non-blocking, best-effort)
                val probes = try {
                    appModel.cachedDashboardReportsV2().mapNotNull { r ->
                        val p = r.probe ?: return@mapNotNull null
                        Probe(
                            uuid = p.uuid,
                            name = p.name,
                            latitude = p.latitude,
                            longitude = p.longitude,
                            radiusKm = 100,
                            enabled = true,
                            isMain = false,
                            isSkysentinel = false,
                            isVirtual = false,
                        )
                    }
                } catch (_: Exception) {
                    emptyList()
                }

                // Single API call: estimatesData now returns projects with
                // topActivities + intensityReports per zone
                val estimatesData: List<ApiEstimatesData> = try {
                    api.estimatesData()
                } catch (_: Exception) {
                    emptyList()
                }
                val items = buildItems(estimatesData, probes)
                updateState { copy(isLoading = false, items = items, probes = probes) }
            } catch (e: Exception) {
                updateState { copy(isLoading = false, error = e.message) }
            }
        }
    }

    private fun buildItems(
        estimatesData: List<ApiEstimatesData>,
        probes: List<Probe>,
    ): List<ProjectWithStats> {
        return estimatesData.map { item ->
            val project = item.project
            val top = item.topActivities
            val math1 = item.intensityReports1km
            val math2 = item.intensityReports2km

            val nearest = probes.minByOrNull {
                haversineKm(it.latitude, it.longitude, project.latitude, project.longitude)
            }
            val nearestDist = nearest?.let {
                haversineKm(it.latitude, it.longitude, project.latitude, project.longitude)
            } ?: Double.MAX_VALUE

            // Use server-side cardinal counts (authoritative)
            val acts1Km = item.cardinal1km
            val acts2Km = item.cardinal2km

            ProjectWithStats(
                project = project,
                nearestProbe = nearest,
                nearestProbeDistanceKm = nearestDist,
                activities1Km = acts1Km,
                activities2Km = acts2Km,
                top = top,
                lastAlertSeconds = top.maxOfOrNull { it.activity.recTimestampSeconds } ?: 0L,
                stats1Km = mathReportToZoneStats(math1),
                stats2Km = mathReportToZoneStats(math2),
            )
        }.sortedByDescending { it.lastAlertSeconds }
    }

    private fun mathReportToZoneStats(math: DashboardImpactReportV2.MathReport?): ZoneStats {
        if (math == null) return ZoneStats()
        val hasNeg = math.avgNegIntensity != 0.0
        val hasPos = math.avgPosIntensity != 0.0
        return ZoneStats(
            avgNegKa = if (hasNeg) abs(math.avgNegIntensity) / 1000.0 else 0.0,
            maxNegKa = if (math.minIntensity < 0) abs(math.minIntensity.toDouble()) / 1000.0 else 0.0,
            maxNegDate = if (math.minIntensity < 0) {
                math.timestampMinSeconds?.let { formatTimestamp(it) } ?: ""
            } else "",
            avgPosKa = if (hasPos) math.avgPosIntensity / 1000.0 else 0.0,
            maxPosKa = if (math.maxIntensity > 0) math.maxIntensity.toDouble() / 1000.0 else 0.0,
            maxPosDate = if (math.maxIntensity > 0) {
                math.timestampMaxSeconds?.let { formatTimestamp(it) } ?: ""
            } else "",
        )
    }

    private fun formatTimestamp(seconds: Long): String {
        val dt = DateTime.fromUnixMillis(seconds * 1000).local
        val d = dt.dayOfMonth.toString().padStart(2, '0')
        val m = dt.month1.toString().padStart(2, '0')
        val yr = dt.yearInt.toString()
        val h = dt.hours.toString().padStart(2, '0')
        val min = dt.minutes.toString().padStart(2, '0')
        return "$d/$m/$yr $h:$min"
    }

    fun setProbeFilter(probeUuid: String?) = updateState { copy(selectedProbeUuid = probeUuid) }

    fun setIncidenceFilter(filter: ProjectsIncidenceFilter) =
        updateState { copy(incidenceFilter = filter) }

    fun setSearchQuery(query: String) = updateState { copy(searchQuery = query) }

    fun setSortOrder(order: ProjectsSortOrder) = updateState { copy(sortOrder = order) }

    companion object {
        fun haversineKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
            val r = 6371.0
            val dLat = (lat2 - lat1) * PI / 180.0
            val dLon = (lon2 - lon1) * PI / 180.0
            val a = sin(dLat / 2).pow(2) +
                    cos(lat1 * PI / 180.0) * cos(lat2 * PI / 180.0) * sin(dLon / 2).pow(2)
            return r * 2 * asin(sqrt(a))
        }
    }
}
